// @file Common content handling logic for both drop and paste operations
// This composable provides shared functionality used by:
// - useDropToCreatePost.ts (for drag & drop operations)
// - usePasteToCreatePost.ts (for paste operations)
import { emojiUrl } from '@@/bits/emoji'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getAttachmentsFromDraft, MAX_PHOTO_ALBUM_ATTACHMENTS } from '@@/bits/post_drafts'
import { useComposerModalAlertStore } from '@@/pinia/composer_modal_alert'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { DraftPost } from '@@/types'
import { usePhotoAlbumEditor } from '@@/vuecomposables/usePhotoAlbumEditor'
import { isEmpty } from 'es-toolkit/compat'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

interface PostPosition {
  top: number
  left: number
}

interface ContentDropOptions {
  /**
   * Optional callback executed *before* showing the default alert
   * when a file is handled while the active draft already has an attachment.
   * If this function is provided and returns `false`, the default alert and
   * replacement logic will be skipped. If it returns `true` or is not provided,
   * the default logic proceeds.
   * @param file The file that was handled.
   * @returns {boolean} Whether to proceed with the default alert/logic.
   */
  onFileDropWithExistingAttachment?: (file: File) => boolean
}

/**
 * Common content handling logic that can be used by both drop and paste operations.
 * This composable is shared between useDropToCreatePost and usePasteToCreatePost.
 */
export const useContentDrop = ({ onFileDropWithExistingAttachment }: ContentDropOptions = {}): {
  handleFilesDrop: typeof handleFilesDrop
  handleLinkDrop: typeof handleLinkDrop
  handleTextDrop: typeof handleTextDrop
  handleSingleFileIfActiveDraftIsPresent: typeof handleSingleFileIfActiveDraftIsPresent
  handleMultipleFileIfActiveDraftIsPresent: typeof handleMultipleFileIfActiveDraftIsPresent
  handleSingleFileIfNoActiveDraft: typeof handleSingleFileIfNoActiveDraft
  handleMultipleFileIfNoActiveDraft: typeof handleMultipleFileIfNoActiveDraft
} => {
  const { openComposerAlert } = useComposerModalAlertStore()
  const { createPostImmediately } = useSurfacePostsStore()

  const surfaceDraftsStore = useSurfaceDraftsStore()
  const {
    startNewDraft,
    updateActiveDraft,
    uploadFileActiveDraft,
    removeDraftAttachment,
    replaceActiveDraftUploadingFile,
    replaceActiveDraftAttachmentWithLink,
  } = surfaceDraftsStore
  const { activeDraft, activeDraftUploadingFile, showPublishAllDraftsPopover, isAnyDraftActive } =
    storeToRefs(surfaceDraftsStore)
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()

  const { onUploadStart, selectFiles } = usePhotoAlbumEditor()

  const activeDraftHasAttachmentOrUploadingFile = computed(
    () => !isEmpty(activeDraft.value?.attachment) || activeDraftUploadingFile.value != null,
  )
  const activeDraftHasPhotoAlbumAttachment = computed(() => {
    const attachments = getAttachmentsFromDraft(activeDraft.value as DraftPost)
    return attachments.length > 1
  })
  const activeDraftHasSingleImageAttachment = computed(() => {
    return (
      !activeDraftHasPhotoAlbumAttachment.value &&
      !isEmpty(activeDraft.value?.attachment) &&
      surfaceAttachmentsStore.getLinkAttributesForUrl(activeDraft.value?.attachment as string)?.content_category ===
        'photo'
    )
  })

  /**
   * Creates a new post for a single file, slightly offset from each other.
   * @param file The file to create a post for.
   * @param position The position where the file was placed (only used for drop operations).
   * @param index The index of the file in the array of files.
   */
  const createPostImmediatelyAtPosition = ({
    file,
    position = { top: 0, left: 0 },
    index = 0,
  }: {
    file: File
    position?: PostPosition
    index?: number
  }): void => {
    const newPost = {
      attributes: {
        file,
        // Place each post a few pixels away from each other.
        top: position.top + index * 16,
        left: position.left + index * 16,
      },
    }
    createPostImmediately(newPost)
  }

  /**
   * Creates a new post for each file, slightly offset from each other,
   * and shows the "Publish All Drafts" popover.
   * @param files The array of files to create posts for.
   * @param position The position where the files were placed (only used for drop operations).
   */
  const createPostsImmediatelyAtPosition = ({
    files,
    position = { top: 0, left: 0 },
  }: {
    files: File[]
    position?: PostPosition
  }): void => {
    files.forEach((file, index) => {
      createPostImmediatelyAtPosition({ file, position, index })
    })
    showPublishAllDraftsPopover.value = true
  }

  /**
   * Handles multiple files when an active draft is present.
   * Shows an alert informing the user that only one file can be added at a time.
   * If photo album feature is enabled, allows adding multiple images to an existing photo album.
   * @param files The array of files.
   */
  const handleMultipleFileIfActiveDraftIsPresent = (files: File[]): void => {
    const draft = activeDraft.value as DraftPost

    if (isAppUsing('photoAlbum')) {
      if (activeDraftHasPhotoAlbumAttachment.value) {
        const imageFiles = files.filter((file) => file.type.startsWith('image/'))
        const nonImageFiles = files.filter((file) => !file.type.startsWith('image/'))

        if (nonImageFiles.length > 0) {
          openComposerAlert({
            title: __('Unsupported file type'),
            body: __('Only images can be added to the photo album'),
            primaryActionText: __('Okay'),
            primaryActions: [],
          })
          return
        }

        const totalAttachments = getAttachmentsFromDraft(draft).length + imageFiles.length
        if (totalAttachments > MAX_PHOTO_ALBUM_ATTACHMENTS) {
          globalSnackbarStore.setSnackbar({
            message: __('No more than %{count} images can be added to a post at a time', {
              count: MAX_PHOTO_ALBUM_ATTACHMENTS,
            }),
            notificationType: SnackbarNotificationType.error,
          })
          return
        }

        selectFiles({ files: imageFiles })
        return
      } else if (activeDraftHasSingleImageAttachment.value) {
        const filesAreAllImages = files.every((file) => file.type.startsWith('image/'))
        if (!filesAreAllImages) {
          openComposerAlert({
            title: __('Only one attachment type allowed'),
            body: __('You are only allowed to add one attachment type per post'),
          })
          return
        }
        const numImagesThatCanBeAdded = MAX_PHOTO_ALBUM_ATTACHMENTS - getAttachmentsFromDraft(draft).length
        if (files.length > numImagesThatCanBeAdded) {
          globalSnackbarStore.setSnackbar({
            message: __('No more than %{count} images can be added to a post at a time', {
              count: MAX_PHOTO_ALBUM_ATTACHMENTS,
            }),
            notificationType: SnackbarNotificationType.error,
          })
          return
        }
        selectFiles({ files })
        return
      } else {
        const filesAreAllImages = files.every((file) => file.type.startsWith('image/'))
        if (!filesAreAllImages) {
          openComposerAlert({
            title: __('Only one attachment type allowed'),
            body: __('You are only allowed to add one attachment type per post'),
          })
          return
        }

        if (files.length > MAX_PHOTO_ALBUM_ATTACHMENTS) {
          globalSnackbarStore.setSnackbar({
            message: __('No more than %{count} images can be added to a post at a time', {
              count: MAX_PHOTO_ALBUM_ATTACHMENTS,
            }),
            notificationType: SnackbarNotificationType.error,
          })
          return
        }

        selectFiles({ files })
        return
      }
    }

    openComposerAlert({
      title: __('Multiple files added'),
      body: __('While writing a post only one file can be added at a time'),
      primaryActionText: __('Okay'),
      primaryActions: [],
    })
  }

  /**
   * Handles a single file when an active draft is present.
   *
   * Photo Album handling:
   * - If photo album feature is enabled:
   *   - Checks photo album attachment limit and shows error if exceeded
   *   - For existing photo albums or single image posts:
   *     - If file is an image: adds it to the photo album
   *     - If file is not an image and there's a photo album: shows unsupported file type alert
   *
   * Regular file handling:
   * - If the draft already has an attachment or an upload is in progress,
   *   it prompts the user to confirm replacement (respecting the optional callback).
   * - Otherwise, it uploads the file to the active draft.
   *
   * @param fileToUpload The single file.
   */
  const handleSingleFileIfActiveDraftIsPresent = (fileToUpload: File): void => {
    const draft = activeDraft.value as DraftPost
    if (isAppUsing('photoAlbum')) {
      if (
        activeDraftHasPhotoAlbumAttachment.value &&
        getAttachmentsFromDraft(draft).length === MAX_PHOTO_ALBUM_ATTACHMENTS
      ) {
        globalSnackbarStore.setSnackbar({
          message: __('No more than %{count} images can be added to a post at a time', {
            count: MAX_PHOTO_ALBUM_ATTACHMENTS,
          }),
          notificationType: SnackbarNotificationType.error,
        })
        return
      }

      if (
        (activeDraftHasSingleImageAttachment.value || activeDraftHasPhotoAlbumAttachment.value) &&
        fileToUpload.type.startsWith('image/')
      ) {
        onUploadStart({ file: fileToUpload })
        return
      } else if (activeDraftHasPhotoAlbumAttachment.value && !fileToUpload.type.startsWith('image/')) {
        openComposerAlert({
          title: __('Unsupported file type'),
          body: __('Only images can be added to the photo album'),
          primaryActionText: __('Okay'),
          primaryActions: [],
        })
        return
      }
    }

    // Ask if user wants to replace the current attachment.
    if (activeDraftHasAttachmentOrUploadingFile.value) {
      // Check if a custom handler is provided and if it signals to stop
      let proceed = true
      if (onFileDropWithExistingAttachment != null) {
        proceed = onFileDropWithExistingAttachment(fileToUpload)
      }
      if (!proceed) return

      // Only proceed with the default alert if the callback didn't return false
      const primaryActions =
        activeDraft.value?.attachment != null
          ? [
              // An attachment already exists
              // -> remove and upload the new one.
              () => {
                if (activeDraft.value?.cid != null) removeDraftAttachment(activeDraft.value.cid)
              },
              () => uploadFileActiveDraft({ file: fileToUpload }),
            ]
          : [
              // An attachment is being uploaded,
              // when the user click "Replace", it can either be still uploading or already uploaded and added to activeDraft.attachment.
              () => replaceActiveDraftUploadingFile({ file: fileToUpload }),
            ]
      openComposerAlert({
        title: __('An attachment already exists'),
        body: __('Are you sure you want to replace your current attachment?'),
        primaryActionText: __('Replace'),
        secondaryActionText: __('Cancel'),
        secondaryActions: [],
        primaryActions,
      })
      return
    }

    uploadFileActiveDraft({ file: fileToUpload })
  }

  /**
   * Handles a single file when no draft is active.
   * Creates a new draft with the file as an attachment at the specified position.
   * @param file The single file.
   * @param position The position where the file was placed (only used for drop operations).
   */
  const handleSingleFileIfNoActiveDraft = (file: File, position: PostPosition = { top: 0, left: 0 }): void => {
    const attributes = { file, ...position }
    const activeDraftCid = activeDraft.value?.cid
    const shouldStartEditing = activeDraftCid == null
    startNewDraft({ attributes, shouldStartEditing })
  }

  /**
   * Handles multiple files when no draft is active.
   * Creates minimized posts immediately for each file, slightly offset from each other,
   * and shows the "Publish All Drafts" popover.
   * @param files The array of files.
   * @param position The position where the files were placed (only used for drop operations).
   */
  const handleMultipleFileIfNoActiveDraft = (files: File[], position: PostPosition = { top: 0, left: 0 }): void => {
    if (isAppUsing('photoAlbum')) {
      const filesAreAllImages = files.every((file) => file.type.startsWith('image/'))
      if (filesAreAllImages) {
        if (files.length <= MAX_PHOTO_ALBUM_ATTACHMENTS) {
          globalConfirmationDialogStore.openConfirmationDialog({
            iconSrc: emojiUrl('svg', '1f3dd'), // island emoji
            buttonScheme: OzConfirmationDialogBoxButtonScheme.PrimaryAndSecondary,
            title: __('Add images'),
            body: __('Add all images to one post or create separate posts for each.'),
            confirmButtonText: __('Single post'),
            secondaryActionButtonText: __('Separate posts'),
            forceFullWidthButtons: true,
            afterConfirmActions: [
              () => {
                // Create a new draft and upload all images to it
                startNewDraft({ attributes: { ...position }, shouldStartEditing: true })
                selectFiles({ files })
              },
            ],
            afterSecondaryActionActions: [
              () => {
                // Create a new post for each image
                createPostsImmediatelyAtPosition({ files, position })
              },
            ],
          })
          return
        }
      }
    }

    createPostsImmediatelyAtPosition({ files, position })
  }

  /**
   * Main handler for file operations.
   * Determines if a draft is active and the number of files,
   * then delegates to the appropriate specialized handler function.
   * @param files The array of files.
   * @param position The position where the files were placed (only used for drop operations).
   */
  const handleFilesDrop = async ({
    files,
    position = { top: 0, left: 0 },
  }: {
    files: File[]
    position?: PostPosition
  }): Promise<void> => {
    if (files.length === 0) return

    if (isAnyDraftActive.value) {
      if (
        activeDraft.value?.attachment != null &&
        surfaceAttachmentsStore.getLinkAttributesForUrl(activeDraft.value?.attachment) == null
      ) {
        await surfaceAttachmentsStore.fetchAndStoreLinkAttributes(activeDraft.value?.attachment)
      }

      if (files.length === 1) {
        handleSingleFileIfActiveDraftIsPresent(files[0])
      } else {
        handleMultipleFileIfActiveDraftIsPresent(files)
      }
    } else {
      // No active draft, create new posts
      if (files.length === 1) {
        handleSingleFileIfNoActiveDraft(files[0], position)
      } else {
        handleMultipleFileIfNoActiveDraft(files, position)
      }
    }
  }

  const handleLinkDrop = ({
    urls,
    position = { top: 0, left: 0 },
  }: {
    urls: string[]
    position?: PostPosition
  }): void => {
    if (urls.length === 0) return

    if (isAnyDraftActive.value) {
      // For links, we conveniently pick the first one to add as an attachment to the active draft.
      const link = urls[0]

      // Ask if user wants to replace the current attachment.
      if (activeDraftHasAttachmentOrUploadingFile.value) {
        openComposerAlert({
          title: __('An attachment already exists'),
          body: __('Are you sure you want to replace your current attachment?'),
          primaryActionText: __('Replace'),
          secondaryActionText: __('Cancel'),
          secondaryActions: [],
          primaryActions: [() => replaceActiveDraftAttachmentWithLink({ link })],
        })
        return
      }

      updateActiveDraft({
        attachment: link,
        wish_content: {
          attachment_props: {
            url: link,
          },
          is_processed: false,
        },
      })
      return
    }

    if (urls.length === 1) {
      // There's only 1 link, create a new draft and add it inside.
      const attachment = urls[0]
      const attributes = { attachment, ...position }
      const activeDraftCid = activeDraft.value?.cid
      const shouldStartEditing = activeDraftCid == null
      startNewDraft({ attributes, shouldStartEditing })
    } else {
      // Add the links in minimized drafts.
      urls.forEach((attachment, index) => {
        const newPost = {
          attributes: {
            attachment,
            // Place each post a few pixels away from each other.
            top: position.top + index * 16,
            left: position.left + index * 16,
          },
        }
        createPostImmediately(newPost)
      })
    }
  }

  const handleTextDrop = ({
    text,
    position = { top: 0, left: 0 },
  }: {
    text: string | null
    position?: PostPosition
  }): void => {
    if (text == null) return

    if (isAnyDraftActive.value) {
      // Append the text to the draft body.
      updateActiveDraft({
        body: (activeDraft.value?.body ?? '') + '<br/>' + text,
      })
      return
    }

    const attributes = { body: text, ...position }
    const activeDraftCid = activeDraft.value?.cid
    const shouldStartEditing = activeDraftCid == null
    startNewDraft({ attributes, shouldStartEditing })
  }

  return {
    handleFilesDrop,
    handleLinkDrop,
    handleTextDrop,
    handleSingleFileIfActiveDraftIsPresent,
    handleMultipleFileIfActiveDraftIsPresent,
    handleSingleFileIfNoActiveDraft,
    handleMultipleFileIfNoActiveDraft,
  }
}
